import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

const Toast = ({ toast }) => {
  const toastKey = useMemo(() => {
    if (!toast || !toast.message) return '';
    return `${toast.type || 'success'}::${toast.message}`;
  }, [toast]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [toastKey]);

  useEffect(() => {
    if (!toast || !toast.message || dismissed) return undefined;
    const handleEsc = (e) => {
      const key = String(e?.key || '').toLowerCase();
      const codeName = String(e?.code || '').toLowerCase();
      const code = Number(e?.keyCode || e?.which || 0);
      const ignoreUntil = Number(window?.__AOT_TOAST_ESC_IGNORE_UNTIL__ || 0);
      if (Number.isFinite(ignoreUntil) && Date.now() < ignoreUntil) {
        return;
      }
      if (key === 'escape' || key === 'esc' || codeName === 'escape' || code === 27) {
        setDismissed(true);
      }
    };
    // Слушаем и keydown, и keyup на window/document в capture-режиме:
    // в некоторых модалках Esc перехватывается на одном из этапов.
    window.addEventListener('keydown', handleEsc, { capture: true });
    window.addEventListener('keyup', handleEsc, { capture: true });
    document.addEventListener('keydown', handleEsc, { capture: true });
    document.addEventListener('keyup', handleEsc, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleEsc, { capture: true });
      window.removeEventListener('keyup', handleEsc, { capture: true });
      document.removeEventListener('keydown', handleEsc, { capture: true });
      document.removeEventListener('keyup', handleEsc, { capture: true });
    };
  }, [toast, dismissed]);

  useEffect(() => {
    if (!toast || !toast.message || dismissed) return undefined;
    const handleDismissEvent = () => setDismissed(true);
    window.addEventListener('aot-toast-dismiss', handleDismissEvent);
    return () => {
      window.removeEventListener('aot-toast-dismiss', handleDismissEvent);
    };
  }, [toast, dismissed]);

  useEffect(() => {
    if (!toast || !toast.message || !toast.sticky || dismissed) return undefined;
    const dismissOnUserAction = () => setDismissed(true);
    window.addEventListener('pointerdown', dismissOnUserAction, { capture: true });
    window.addEventListener('keydown', dismissOnUserAction, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', dismissOnUserAction, { capture: true });
      window.removeEventListener('keydown', dismissOnUserAction, { capture: true });
    };
  }, [toast, dismissed]);

  if (!toast || !toast.message || dismissed) {
    return null;
  }

  const { message, type = 'success' } = toast;
  const icon = type === 'success' ? '✓' : (type === 'info' ? 'ℹ' : '✕');

  const toastNode = (
    <div className={`toast toast-${type}`}>
      <div className="toast-content">
        <span className="toast-icon">{icon}</span>
        <span className="toast-message">{message}</span>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return toastNode;
  }

  return createPortal(toastNode, document.body);
};

export default Toast;
